export type InvoiceType = "Rental" | "Upfront" | "Advance" | "Consolidated" | "Penalty" | "Installation" | "Insurance" | "Other";
export type InvoiceStatus = "Draft" | "Pending" | "Paid" | "Partially Paid" | "Overdue";

export interface InvoiceLine {
  id: string;
  scheduleID: string;
  scheduleLineID: string;
  description: string;
  baseAmount: number;
  gstAmount: number;
  tdsAmount: number;
  totalAmount: number;
  fromDate: string;
  toDate: string;
  assets?:any[];
  periodNo?: string;  
}

export interface PaymentRecord {
  id: string;
  amount: number;
  date: string;
  payer?: 'paidByAstrafin' | 'payToLender' | 'payToAstrafin';
  proofUrl?: string;
  paymentMode?: string;
  reference?: string;
  notes?: string;
  paidSource?: 'manual' | 'ASTRAFIN_EXCEL';
}

export interface invoiceTableData {
    customerName? : string;
    amount : number;
    assignedToLender : boolean;
    periodNo : string;
    dueDate : string;
    invoiceDate : string;
    invoiceId : string;
    lenderAvatar : string;
    lenderName : string;
    nach : boolean;
    paid : number;
    rentalScheduleDisplayId : string;
    rsInvoiceCode : string;
    status : string;
    externalAckNo?: string;
    assignedStatus?: "A" | "P" | "N" | null;
}

export interface Invoice {
  invoiceId?: string;
  displayId ?: string;
  rsInvoiceCode?: string;
  scheduleId?: string; // Optional for manual invoices
  customerId: string;
  customer: {
    customerId: string;
    name: string;
    gstin?: string;
    email?: string;
  };
  type: InvoiceType;
  isManual: boolean; // Flag to distinguish manual vs auto-generated
  category?: string; // For manual invoices (e.g., "Late Payment Penalty")
  invoiceDate: string;
  dueDate: string;
  baseRounded: number;
  gstRounded: number;
  gstRate: number;
  tdsRounded: number;
  grandTotalRounded: number;
  totalRoundedAfterTDS: number;
  lineItems?: InvoiceLine[];
  payments: PaymentRecord[];
  status?: InvoiceStatus;
  assignedToLender: boolean;
  noaId?: string;
  lenderName?: string;
  scheduleLineId?: string;
  fundingMode?: "A" | "B" | "C";
  periodNo ? : string;
  rentalScheduleDisplayId ? : string;
  fundingLimitPercent?: number;
  notes?: string; // Additional notes for manual invoices
  nachIsActive?: boolean;
  nachType?:string;
  nachStartDate?:string;
  billingAddress?:any;
  shippingAddress?:any;
  lenderAccountHolderName?: string;
  lenderAccountNumber?: string;
  lenderAccountType?: string;
  lenderBankName?: string;
  lenderBranchName?: string;
  lenderIfscCode?: string;
  gstType?: string;
  igstAmount?: number;
  cgstAmount?: number;
  sgstAmount?: number;
  utgstAmount?: number;
  lenderAvatar?: string;
  iconImagePath?: string;
  tdsRate?: number;
  amountPayableToAstrafin?: number;
  amountPayableToLender?: number;
  amountPaidByAstrafin?: number;
  amountPaidByLender?: number;
  collectionRoute?: "direct" | "pass_through";
  /** A = fully assigned, P = partial, N / null = not assigned. */
  assignedStatus?: "A" | "P" | "N" | null;
  isRentalEmailSent?: boolean;
  emailSentAt?: string | null;
  lenderBankAccountId?: string;
  externalAckNo?: string;
  externalInvoiceStatus?: string;
  externalAckDate?: string;
  externalQrCodeUrl?: string;
  externalGeneratedAt?: string;
  externalIrn?:string;
  externalCancelledAt?:string;
  externalFailedReason?:string;
}

export function normalizeCollectionRoute(
  value: unknown
): Invoice["collectionRoute"] | undefined {
  if (value == null || value === "") return undefined;
  const normalized = String(value).toLowerCase().replace(/-/g, "_");
  if (normalized === "pass_through") return "pass_through";
  if (normalized === "direct") return "direct";
  return undefined;
}

export function getCollectionRouteLabel(route: NonNullable<Invoice["collectionRoute"]>): string {
  return route === "pass_through" ? "Astrafin (Pass-through)" : "Lender (Direct)";
}

export function getCollectionRouteShortLabel(route: NonNullable<Invoice["collectionRoute"]>): string {
  return route === "pass_through" ? "Astrafin" : "Lender";
}

export function isPassThroughCollectionRoute(
  invoice: Pick<Invoice, "collectionRoute"> & { collection_route?: unknown }
): boolean {
  const route =
    normalizeCollectionRoute(invoice.collectionRoute ?? invoice.collection_route) ??
    invoice.collectionRoute;
  return route === "pass_through";
}

/** Normalize invoice assignment status. Legacy "F" maps to "A". Null/unknown → null (not assigned). */
export function getAssignedStatus(
  invoice: Pick<Invoice, "assignedStatus"> & {
    assignedStatus?: "A" | "P" | "N" | null;
  }
): "A" | "P" | "N" | null {
  const raw = String(invoice.assignedStatus ?? "")
    .trim()
    .toUpperCase();
  if (raw === "A" || raw === "F") return "A";
  if (raw === "P") return "P";
  if (raw === "N") return "N";
  return null;
}

/** Payment note for rental invoice PDF / preview, based on assignment status + funding mode. */
export function getInvoicePaymentNoteText(
  invoice: Pick<
    Invoice,
    | "assignedToLender"
    | "fundingMode"
    | "nachIsActive"
    | "lenderName"
    | "assignedStatus"
    | "collectionRoute"
  > & { collection_route?: unknown; assignedStatus?: "A" | "P" | "N" | null },
  lenderName?: string
): string {
  const name = (lenderName || invoice.lenderName || "the lender").trim() || "the lender";
  const assignedStatus = getAssignedStatus(invoice);

  // null / N = not assigned → full amount to Astrafin
  // also pass-through collection always pays Astrafin
  if (
    !assignedStatus ||
    assignedStatus === "N" ||
    !invoice.assignedToLender ||
    isPassThroughCollectionRoute(invoice)
  ) {
    return "Kindly make payment of the full invoice amount to Astrafin Neosolutions Private Limited.";
  }

  // P = partial assignment
  if (assignedStatus === "P") {
    if (invoice.fundingMode === "A") {
      return `Kindly make the assigned payment to ${name} and the unassigned base rental plus GST, net of TDS, to Astrafin.`;
    }
    if (invoice.fundingMode === "B" || invoice.fundingMode === "C") {
      return `Kindly make the assigned payment to ${name} and the unassigned amount to Astrafin.`;
    }
  }

  // A = fully assigned (existing funding-mode notes)
  if (invoice.fundingMode === "A" && !invoice.nachIsActive) {
    return `Kindly make payment of the base rental to the lender (${name}). The GST component, net of TDS, should be paid to Astrafin.`;
  }
  if (invoice.fundingMode === "A" && invoice.nachIsActive) {
    return `Base rental will be auto-debited by ${name} with active NACH. GST net of TDS should be paid to Astrafin.`;
  }
  if (invoice.fundingMode === "B" && !invoice.nachIsActive) {
    return `Kindly make payment of the base rental plus GST, less TDS withheld, to the lender (${name}).`;
  }
  if (invoice.fundingMode === "B" && invoice.nachIsActive) {
    return `Base + GST - TDS will be auto-debited by ${name} with active NACH.`;
  }
  if (invoice.fundingMode === "C" && !invoice.nachIsActive) {
    return `Kindly make payment of the base rental less TDS, plus GST, to the lender (${name}).`;
  }
  if (invoice.fundingMode === "C" && invoice.nachIsActive) {
    return `Base - TDS + GST will be auto-debited by ${name} with active NACH.`;
  }

  return "";
}

export function getInvoiceStatus(invoice: invoiceTableData): InvoiceStatus {
  const totalPaid = invoice.paid;
  const isOverdue = new Date(invoice.dueDate) < new Date();


  if (totalPaid === 0) {
    return isOverdue ? "Overdue" : "Pending";
  } else if (totalPaid >= invoice.amount) {
    return "Paid"; 
  } else {
    return isOverdue ? "Overdue" : "Partially Paid";
  }
}

export function getInvoiceStatusColor(status: InvoiceStatus): string {
  switch (status) {
    case "Paid":
      return "bg-green-100 text-green-800 border-green-200";
    case "Partially Paid":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "Overdue":
      return "bg-red-100 text-red-800 border-red-200";
    case "Pending":
      return "bg-blue-100 text-blue-800 border-blue-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
}